/* eslint-disable no-unused-vars */
import { Button, CircularProgress } from "@mui/material";
import React from "react";
import { useTailwindBrand } from "../shared";

interface Props {
  label: string;
  variant?: "contained" | "outlined" | "text";
  size?: "small" | "medium" | "large";
  onClick?: (() => void) | ((e: any) => void)
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  color?: "primary" | "secondary" | "error" | "info" | "success" | "warning" | "inherit";
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
  href?: string;
  style?: React.CSSProperties;
  loading?: boolean;
  loadingPosition?: "start" | "end";
  loadingIndicator?: React.ReactNode;
  type?: "submit" | "reset" | "button";
}

export default function ButtonField({
  variant,
  size,
  onClick,
  startIcon,
  endIcon,
  // color,
  fullWidth,
  disabled,
  className,
  style,
  loading = false,
  loadingPosition = "start",
  loadingIndicator,
  label,
  type,
  href
}: Props) {
  const isDisabled = disabled || loading;
  const { brandConfig, classes } = useTailwindBrand()

  const renderLoadingIndicator = () => {
    if (!loading) return null;

    const indicator = loadingIndicator || (
      <CircularProgress
        size={size === "small" ? 16 : size === "large" ? 24 : 20}
        color="inherit"
      />
    );

    return indicator;
  };

  const getStartIcon = () => {
    if (loading && loadingPosition === "start") {
      return renderLoadingIndicator();
    }
    return startIcon;
  };

  const getEndIcon = () => {
    if (loading && loadingPosition === "end") {
      return renderLoadingIndicator();
    }
    return endIcon;
  };

  const renderBgColor = () => {
    switch (variant) {
      case "contained":
        return {
          background: `linear-gradient(135deg, ${brandConfig.colors.primary} 0%, ${brandConfig.colors.primary}E6 100%)`,
          color: "white",
          boxShadow: `0 6px 16px -6px ${brandConfig.colors.primary}99`,
        };
      case "outlined":
        return {
          backgroundColor: "transparent",
          color: brandConfig.colors.primary,
          borderColor: brandConfig.colors.primary,
        };
      default:
        return undefined;
    }
  };

  const sxStyles = {
    fontFamily: brandConfig.fontFamily,
    textTransform: "none",
    borderRadius: "10px",
    transition: "transform .2s ease, box-shadow .2s ease, background-color .2s ease",
    "&:hover": variant === "contained"
      ? {
        background: `linear-gradient(135deg, ${brandConfig.colors.primary} 0%, ${brandConfig.colors.primary} 100%)`,
        boxShadow: `0 10px 24px -8px ${brandConfig.colors.primary}B3`,
        transform: "translateY(-1px)",
      }
      : {
        backgroundColor: "transparent",
        borderColor: variant === "outlined" ? brandConfig.colors.primary : undefined,
      },
    // le dégradé écraserait le style désactivé par défaut de MUI
    "&.Mui-disabled": variant === "contained"
      ? { color: "rgba(255,255,255,0.85)", opacity: 0.55, boxShadow: "none" }
      : undefined,
    ...renderBgColor(),
  };

  const buttonProps: any = {
    variant: variant ?? "contained",
    size,
    onClick,
    startIcon: getStartIcon(),
    endIcon: getEndIcon(),
    fullWidth,
    disabled: isDisabled,
    className: `${className ?? ""} ${classes.primaryHover}`,
    style,
    type: type ?? "button",
  };

  if (href) {
    buttonProps.href = href;
  }

  return (
    <Button
      // loading={loading}
      // loadingPosition="start"
      sx={sxStyles}
      {...buttonProps}
    >
      {label}
    </Button>
  );
}
